import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Button,
  Tooltip,
  Switch
} from '@mui/material';

interface Option {
  id: string;
  label: string;
}

interface ItemOption {
  value: string;
  label: string;
}

interface MappingCellProps<T extends Record<string, any>> {
  label: string;
  field: keyof T;
  mapping: T;
  defaultValues: Partial<Record<keyof T, string>>;
  options: Option[];
  requiredFields?: Array<keyof T>;
  itemOptions?: ItemOption[];
  setMapping: Dispatch<SetStateAction<T>>;
  setDefaultValues: Dispatch<SetStateAction<T>>;
  onSequenceChange?: (value:string) => void;
  onUpdateIfExistsChange?: (value: boolean) => void;
  showSequence?:boolean
}

function MappingCell<T extends Record<string, any>>({
  label,
  field,
  mapping,
  defaultValues,
  options,
  requiredFields = [],
  itemOptions,
  setMapping,
  setDefaultValues,
  onSequenceChange,
  onUpdateIfExistsChange,
  showSequence=false
}: MappingCellProps<T>) {
  const [sequenceValue, setSequenceValue] = useState<string>('');
  const [updateIfExists, setUpdateIfExists] = useState<boolean>(false);
  options = [{ id: '', label: '---' },...options];
  const hasValue = mapping[field] || defaultValues[field];
  const isRequired = requiredFields.includes(field);
  const isError = isRequired && !hasValue;

  const handleMappingChange = (field: keyof T, value: string) => {
    setMapping((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDefaultValueChange = (field: keyof T, value: string) => {
    setDefaultValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSequenceInput = (value: string) => {
    setSequenceValue(value);
    if (onSequenceChange) {
      onSequenceChange(value);
    }
  };

  const handleUpdateIfExistsChange = (checked: boolean) => {
    setUpdateIfExists(checked);
    if (onUpdateIfExistsChange) {
      onUpdateIfExistsChange(checked);
    }
  };

  return (
    <TableCell
      sx={{
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        padding: '8px',
        minWidth: '150px',
        border: isError ? '1px solid red' : 'none',
        verticalAlign: 'top',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Select chính */}
        <FormControl fullWidth size="small" error={isError}>
          <InputLabel id={`${String(field)}-label`} sx={{ fontSize: '0.75rem' }}>
            {label} {isRequired && '*'}
          </InputLabel>
          <Select
            labelId={`${String(field)}-label`}
            id={`${String(field)}-select`}
            value={mapping[field] || ''}
            label={label}
            onChange={(e) => handleMappingChange(field, e.target.value)}
            sx={{ fontSize: '0.75rem' }}
          >
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id} sx={{ fontSize: '0.75rem' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Select phụ hoặc TextField */}
        {itemOptions ? (
          <FormControl fullWidth size="small" error={isError}>
            <Select
              id={`${String(field)}-default-select`}
              value={defaultValues[field] || ''}
              onChange={(e) => handleDefaultValueChange(field, e.target.value)}
              sx={{ fontSize: '0.75rem' }}
            >
              <MenuItem value="---" sx={{ fontSize: '0.75rem' }}>
                ---
              </MenuItem>
              {itemOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.75rem' }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              id={`${String(field)}-default-textfield`}
              size="small"
              value={defaultValues[field] || ''}
              onChange={(e) => handleDefaultValueChange(field, e.target.value)}
              placeholder="default value"
              error={isError}
              inputProps={{
                style: { fontSize: '0.75rem' },
              }}
              InputProps={{
                sx: {
                  '& input::placeholder': {
                    fontSize: '0.75rem',
                  },
                },
              }}
            />
          </Box>
        )}
        
        
        {/* Sequence input - always rendered to maintain layout */}
        {showSequence && (
          <Box sx={{ display: 'flex', gap: 1, height: onSequenceChange ? 'auto' : '40px' }}>
            {onSequenceChange ? (
              <Tooltip 
                title="Enter sequence format: [Prefix][Number] (e.g., AB00001). The number will auto-increment for each row."
                placement="top"
                arrow
              >
                <TextField
                  size="small"
                  placeholder="Sequence number"
                  value={sequenceValue}
                  onChange={(e) => handleSequenceInput(e.target.value)}
                  sx={{ flex: 1 }}
                  inputProps={{
                    style: { fontSize: '0.75rem' },
                  }}
                />
              </Tooltip>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
          </Box>
        )}
        
        {/* Update if exists switch */}
        {onUpdateIfExistsChange && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip 
              title="Update existing records if they exist"
              placement="top"
              arrow
            >
              <Switch
                checked={updateIfExists}
                onChange={(e) => handleUpdateIfExistsChange(e.target.checked)}
              />
            </Tooltip>
          </Box>
        )}
      </Box>
    </TableCell>
  );
}

export default MappingCell;
